import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Coffee, Cog, ClipboardCheck, Phone } from 'lucide-react';
import ClientLogoStrip, { chunkClientsBySix } from '../components/ClientLogoStrip';
import {
  CLIENT_SECTORS,
  getSectorLogoRow,
  getSectorRemainingClients,
  getTotalClientCount,
} from '../data/clientsData';

const sectorIcon = {
  flask: FlaskConical,
  coffee: Coffee,
  cog: Cog,
};

const Clients = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const totalClients = getTotalClientCount();

  return (
    <div className="clients-page">
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-900 py-20">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container relative z-10 mx-auto px-6">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-6 inline-block rounded-small border border-secondary-400/40 bg-secondary-500/20 px-6 py-2 backdrop-blur-sm">
              <span className="font-heading text-sm font-bold uppercase tracking-wider text-secondary-300">
                Trusted by Industry Leaders
              </span>
            </div>
            <h1 className="mb-6 font-heading text-5xl font-bold uppercase tracking-wide text-white md:text-6xl">
              Our Major <span className="text-secondary-400">Clients</span>
            </h1>
            <p className="mx-auto max-w-3xl font-body text-xl leading-relaxed text-neutral-200">
              Delivering excellence across pharmaceuticals, food &amp; beverages, and automotive industries with proven
              track record and trusted partnerships.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200 bg-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="text-center">
              <div className="mb-2 font-heading text-5xl font-bold text-primary-600">{totalClients}+</div>
              <div className="font-body text-sm uppercase tracking-wider text-neutral-600">Total Clients</div>
            </div>
            <div className="text-center">
              <div className="mb-2 font-heading text-5xl font-bold text-secondary-600">3</div>
              <div className="font-body text-sm uppercase tracking-wider text-neutral-600">Industry Sectors</div>
            </div>
            <div className="text-center">
              <div className="mb-2 font-heading text-5xl font-bold text-primary-600">25+</div>
              <div className="font-body text-sm uppercase tracking-wider text-neutral-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="mb-2 font-heading text-5xl font-bold text-secondary-600">500+</div>
              <div className="font-body text-sm uppercase tracking-wider text-neutral-600">Projects Completed</div>
            </div>
          </div>
        </div>
      </section>

      {CLIENT_SECTORS.map((sector, index) => {
        const Icon = sectorIcon[sector.icon] || FlaskConical;
        const headerBg = sector.accent === 'secondary' ? 'bg-secondary-600' : 'bg-primary-600';
        const sectionBg = index % 2 === 0 ? 'bg-background-100' : 'bg-white';
        const logoRow = getSectorLogoRow(sector);
        const remaining = getSectorRemainingClients(sector);
        const allClientRows = chunkClientsBySix([...logoRow, ...remaining]);

        return (
          <section key={sector.id} className={`py-16 ${sectionBg}`}>
            <div className="container mx-auto max-w-7xl px-4 sm:px-6">
              <div className="mb-10 text-center">
                <div className={`mb-4 inline-flex items-center gap-3 rounded-small px-6 py-3 ${headerBg}`}>
                  <Icon className="h-6 w-6 text-white" />
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-wide text-white sm:text-3xl">
                    {sector.title}
                  </h2>
                </div>
                <p className="mx-auto max-w-3xl font-body text-lg leading-relaxed text-neutral-600">
                  {sector.description}
                </p>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {allClientRows.map((row, rowIndex) => (
                  <ClientLogoStrip
                    key={`${sector.id}-row-${rowIndex}`}
                    clients={row}
                    accent={sector.accent}
                  />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <section className="relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-900 py-16">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="container relative z-10 mx-auto px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-6 font-heading text-4xl font-bold uppercase tracking-wide text-white">
              Join Our Growing <span className="text-secondary-400">Client Family</span>
            </h2>
            <p className="mb-8 font-body text-xl leading-relaxed text-neutral-200">
              Experience the same quality and reliability that industry leaders trust. Let&apos;s discuss your flooring
              requirements.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-small bg-secondary-500 px-8 py-4 font-heading text-base font-bold uppercase tracking-wider text-white shadow-lg transition-all hover:bg-secondary-600 hover:shadow-custom-hover"
              >
                <ClipboardCheck className="h-5 w-5" />
                Request Quote
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-small bg-white px-8 py-4 font-heading text-base font-bold uppercase tracking-wider text-primary-700 transition-all hover:bg-neutral-100"
              >
                <Phone className="h-5 w-5" />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Clients;
